import {
  useRef,
  useState,
  type DragEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Paper, TextField, Typography } from '@mui/material'

import { AttachmentAddButtons } from '@/apps/latest/features/attachments/AttachmentAddButtons'
import { AttachmentsPanel } from '@/apps/latest/features/attachments/AttachmentsPanel'
import { sendAttachment } from '@/apps/latest/features/attachments/attachmentUploads'
import { noteSx } from '@/apps/latest/features/attachments/attachmentStyles'
import { useAttachmentPicker } from '@/apps/latest/features/attachments/useAttachmentPicker'
import {
  MemoPanel,
  textToItems,
  type MemoFlush,
} from '@/apps/latest/features/yakureki/components/MemoPanel'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useUpdateMemoMutation } from '@/features/yakureki'
import { RADIUS } from '@/shared/styles/radii'

interface Props {
  yakurekiId: string
  memoItems: string[]
  // 「作る」の前にメモの保存を待ち合わせる口 (page が持つ)
  memoFlushRef: MutableRefObject<MemoFlush | null>
  // カルテでメモを書き始めた 1 手で記録が生まれて来たとき、続きから打てるようにする
  memoAutoFocus?: boolean
}

// 作る前の材料: 添付資料 (問診票・指示書・食事の写真) とメモを横に並べる (タブにしない)。題は「今回の」を付ける
// (下の回の行が前回を出す間は、前回の報告書の右の列にもメモがある)。
// 録る前も、録っている間も、文字起こしを待つ間も足せる。狭い画面では縦に積む。
export const NutritionMaterials = ({
  yakurekiId,
  memoItems,
  memoFlushRef,
  memoAutoFocus,
}: Props) => (
  <Columns>
    <Section>
      {/* 題「添付資料」は panel が持つ (題の行の右端に足す口の icon 2 つ) */}
      <AttachmentsPanel yakurekiId={yakurekiId} title="今回の添付資料" />
    </Section>
    <Section title="今回のメモ">
      <MemoPanel
        yakurekiId={yakurekiId}
        memoItems={memoItems}
        flushRef={memoFlushRef}
        autoFocus={memoAutoFocus}
      />
    </Section>
  </Columns>
)

interface BeforeRoundProps {
  // 今回を始める (記録を作る。同じ画面の 2 手目は同じ記録を待つ)
  start: () => Promise<string>
  // 記録ができたらその画面へ移る (memo = メモを書き始めた手)
  onStarted: (eventId: string, by: 'attachment' | 'memo') => void
}

// 今回がまだ無いカルテの材料 (design-book ADR-0084)。形は上と同じで、資料を足す・メモを書き始めるの最初の 1 手で
// 今回の記録を作り、資料はその記録へ送り (送っている途中の資料は記録の画面の panel に続けて出る)、メモはその記録に
// 保存してから記録の画面へ移る。開いただけ・見ただけでは何も作らない。
export const NutritionMaterialsBeforeRound = ({
  start,
  onStarted,
}: BeforeRoundProps) => {
  const [reason, setReason] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [memo, setMemo] = useState('')
  const memoRef = useRef('')
  const updateMemo = useUpdateMemoMutation()
  const picker = useSendBeforeRound(start, onStarted, setReason)

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    setReason(null)
    void picker.addFiles(Array.from(e.dataTransfer.files))
  }

  // メモは最初の 1 字で記録を作り、打った字を保存してから移る (移った先のメモ欄が続きを受ける)。
  // 保存の往復の間に打った字は保存に入っていないので、欄が保存した字と同じになるまで保存し直してから移る
  // (比べてから移るまでの間に打鍵は割り込まない)
  const writeMemo = (text: string) => {
    const first = memoRef.current === '' && text !== ''
    memoRef.current = text
    setMemo(text)
    if (!first) return
    void start()
      .then(async (eventId) => {
        let saved: string
        do {
          saved = memoRef.current
          await updateMemo.mutateAsync({
            yakurekiId: eventId,
            memoItems: textToItems(saved),
          })
        } while (memoRef.current !== saved)
        onStarted(eventId, 'memo')
      })
      .catch(() => {
        // 作れない・保存できない知らせは hook が出す。打った字は欄に残る (次の字でもう一度)
        memoRef.current = ''
      })
  }

  return (
    <Columns>
      <Section>
        <Box
          onDragOver={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={(e: DragEvent<HTMLDivElement>) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDragging(false)
          }}
          onDrop={handleDrop}
          sx={{
            borderRadius: `${RADIUS.md}px`,
            bgcolor: dragging ? colorTokens.bg.muted : 'transparent',
            transition: 'background-color 120ms',
          }}
        >
          <Typography
            variant="subtitle2"
            component="h2"
            sx={{ mb: 1.5, fontWeight: 600 }}
          >
            今回の添付資料
          </Typography>
          {reason && (
            <Typography sx={{ ...noteSx, mb: 1 }}>{reason}</Typography>
          )}
          <AttachmentAddButtons
            onCapture={() => {
              setReason(null)
              picker.openCamera()
            }}
            onPick={() => {
              setReason(null)
              picker.openFilePicker()
            }}
          />
          {picker.elements}
        </Box>
      </Section>
      <Section title="今回のメモ">
        {/* 見た目は MemoPanel と同じ (記録ができたら MemoPanel に替わる) */}
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={20}
          value={memo}
          onChange={(e) => writeMemo(e.target.value)}
          placeholder="指導メモを書く"
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: (theme) => theme.typography.body2.fontSize,
              lineHeight: 1.7,
              alignItems: 'flex-start',
              p: 0,
            },
          }}
        />
      </Section>
    </Columns>
  )
}

// 資料の最初の 1 枚で記録を作って送り、記録の画面へ移る。続けて選んだ資料も同じ記録へ送る (start が同じ記録を返す)。
const useSendBeforeRound = (
  start: () => Promise<string>,
  onStarted: BeforeRoundProps['onStarted'],
  setReason: (reason: string | null) => void
) => {
  const queryClient = useQueryClient()
  const moved = useRef(false)
  const send = (blob: Blob) =>
    start()
      .then((eventId) => {
        void sendAttachment(queryClient, eventId, blob)
        if (!moved.current) {
          moved.current = true
          onStarted(eventId, 'attachment')
        }
      })
      .catch(() => {
        // 作れない知らせは hook が出す。選び直せばもう一度作る
      })
  return useAttachmentPicker({
    onBlob: (blob) => void send(blob),
    onUnreadable: setReason,
  })
}

// 添付資料とメモの横並び (狭い画面では縦に積む)
const Columns = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: 2,
    }}
  >
    {children}
  </Box>
)

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: `${RADIUS.xl}px`, p: 2, minWidth: 0 }}
  >
    {title && (
      <Typography
        variant="subtitle2"
        component="h2"
        sx={{ mb: 1.5, fontWeight: 600 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Paper>
)
